import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { apiClient, unwrapApiResult, withAbortSignal } from "@/lib/api-client";

const SETTINGS_CACHE_KEY = "site-settings-cache-v1";
const SETTINGS_TTL_MS = 5 * 60 * 1000;

function readCachedSettings(): Record<string, string> | undefined {
  try {
    const raw = localStorage.getItem(SETTINGS_CACHE_KEY);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as { data: Record<string, string>; expiresAt: number };
    if (!parsed?.data || Date.now() > parsed.expiresAt) return undefined;
    return parsed.data;
  } catch {
    return undefined;
  }
}

function writeCachedSettings(data: Record<string, string>) {
  try {
    localStorage.setItem(
      SETTINGS_CACHE_KEY,
      JSON.stringify({ data, expiresAt: Date.now() + SETTINGS_TTL_MS })
    );
  } catch {
    // ignore storage failures
  }
}

export function useSiteSettings() {
  return useQuery({
    queryKey: ["site-settings"],
    queryFn: async ({ signal }) => {
      const result = await apiClient.query<any[]>({
        label: "site-settings",
        routeTag: "useSiteSettings",
        timeoutMs: 15_000,
        signal,
        run: (abortSignal) =>
          withAbortSignal(
            supabase.from("site_settings" as any).select("key, value").order("key"),
            abortSignal
          ),
      });

      const data = unwrapApiResult(result) ?? [];
      const map: Record<string, string> = {};
      for (const row of data as any[]) {
        map[row.key] = row.value;
      }
      writeCachedSettings(map);
      return map;
    },
    initialData: readCachedSettings,
    staleTime: SETTINGS_TTL_MS,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
  });
}

export function useUpdateSiteSetting() {
  const qc = useQueryClient();
  return async (key: string, value: string) => {
    const result = await apiClient.query<any>({
      label: "update-site-setting",
      routeTag: "useUpdateSiteSetting",
      timeoutMs: 30_000,
      run: () =>
        (supabase as any)
          .from("site_settings")
          .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: "key" }),
    });

    unwrapApiResult(result);
    qc.invalidateQueries({ queryKey: ["site-settings"] });
  };
}
